import React from 'react';
import renderCallback from 'react-render-callback';

import {translate} from 'iss/strings';
import {Button} from 'iss/widgets/Buttons';
import {BatchOpResultsDialog} from 'iss/widgets/batchOps/BatchOpResultsDialog';
import Modal from 'iss/widgets/bootstrap/Modal';

/** Displays a modal for a batch op operation.
 * Allows for completing the whole batch operation in the same modal including
 * selecting settings for it, showing progress and success/failure messages
 *
 * Props:
 *
 * operation :: OperationDescriptor
 * An object describing the operation to perform and its associated views.
 * The format is:
 * {
 *  id: 'fakeop', // id of the operation, unused
 *  title: 'activationOperations.archive.title', /Translation key for the modal title
 *  progressMessage: "activationOperations.archive.progressMessage", //Translation key for the message to show when the operation is running
 *  components: {
 *    //A component that is given to callbacks for either closing the dialog or starting a batch operation
 *    //The startOperation should be given an observable returning batch op results as a parameter.
 *    //An optional second parameter may be given and is passed to the failure and success components as is
 *    settings: ({onCancel, startOperation}) =>
 *    <div id="settings">
 *      <button id="cancel" onClick={onCancel}></button>
 *      <button id="proceed" onClick={() => startOperation(startOp(), settings)}></button>
 *    </div>,
 *    //Components to show when the operation fails or succeeds
 *    success: ({operationResult, operationTargets, onClose, settings}) => <p id="success">Success!</p>,
 *    failure: ({operationResult, operationTargets, onClose, settings}) => <p id="failure">Failure!</p>,
 *    //Optional footer components to override the default close button shown on fail or success
 *    successFooter: ({operationResult, operationTargets, onClose, settings}) => <button id="close" onClick={onClose} /></div>,
 *    failureFooter: ({operationResult, operationTargets, onClose, settings}) => <button id="close" onClick={onClose} /></div>,
 *  }
 * }
 *
 * operationTargets :: [Object]
 * A list of targets for the operation. Provided for the settings component as a prop.
 *
 * onClose :: Function
 * Callback that is fired if the user tries to close the modal. Provided to the
 * settings component as a prop.
 *
 * onOperationDone :: Function
 * Callback that is fired if the user tries to close the modal after an
 * operation succeeded. This is provided to the success and failure components
 * under the name onClose.
 *
 *
 **/
export class BatchOpRunnerDialog extends React.Component {
  state = {
    operationResults: null,
    settings: null,
  };
  startOperation = (batchOp, settings = {}) => {
    this.setState({settings}, () => {
      batchOp.subscribe((results) => {
        this.setState({operationResults: results});
      });
    });
  };
  closeAndReset = () => {
    this.setState({operationResults: null});
    this.props.onOperationDone();
  };

  render() {
    const {operation, operationTargets, onClose} = this.props;
    const {operationResults, settings} = this.state;
    if (!operation) return null;

    const {components} = operation;

    if (operationResults) {
      const {success, successFooter, failure, failureFooter} = components;
      const renderProps = {
        operationResult: operationResults,
        settings, operationTargets,
        onClose: this.closeAndReset,
      };
      return <BatchOpResultsDialog title={operation.title} operationResults={operationResults}
        inProgressMessage={operation.progressMessage}
        renderSuccess={operationResultContent(success, successFooter, renderProps)}
        renderFailure={operationResultContent(failure, failureFooter, renderProps)}
      />;
    }

    return <Modal title={translate(operation.title)} visible={true} onClose={onClose} size="large">
      {renderCallback(components.settings,
        {operationTargets, onCancel: onClose, startOperation: this.startOperation})
      }
    </Modal>;
  }
}

function operationResultContent(component, footerComponent, props) {
  return <Modal.Contents>
    {{
      body: renderCallback(component, props),
      footer: renderCallback(footerComponent || CloseButtonFooter, props)
    }}
  </Modal.Contents>;
}

function CloseButtonFooter({onClose}) {
  return <Button onClick={onClose} id="close" text={translate('common.close')} />;
}



// WEBPACK FOOTER //
// ./modules/issFrontend/widgets/batchOps/BatchOpRunnerDialog.jsx